import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import Tooltip from "@/components/core/tooltip";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { publicProfileQueryOptions } from "@/features/dashboard/api/queries/public-profile-query";
import { ProfileCaseList } from "@/features/dashboard/components/profile-case-list";
import { ProfileSessions } from "@/features/dashboard/components/profile-sessions";
import { useAuth } from "@/hooks/use-auth";
import { IconAwardFilled, IconDots, IconScale } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/profile")({
	validateSearch: z.object({
		status: z.string().optional(),
		tab: z.string().optional(),
	}),
	loaderDeps: ({ search }) => ({ status: search.status }),
	loader: ({ context: { queryClient, auth }, deps }) => {
		if (!auth.user) {
			throw new Error("User is not authenticated");
		}
		return queryClient.ensureQueryData(publicProfileQueryOptions(auth.user.id as string, deps.status || "all"));
	},
	component: Page,
});

function Page() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const queryParams = new URLSearchParams(window.location.search);
	const [activeTab, setActiveTab] = useState(queryParams.get("status") || "all");
	const [currentTab, setCurrentTab] = useState(queryParams.get("tab") || "cases");

	const profileQuery = useSuspenseQuery(publicProfileQueryOptions(user?.id as string, activeTab));
	const data = profileQuery.data;

	const setUrl = async (status: string) => {
		setActiveTab(status);
		await navigate({
			to: "/profile",
			search: (old) => ({ ...old, status: status }),
			replace: true,
		});
	};

	const setTab = async (tab: string) => {
		setCurrentTab(tab);
		await navigate({
			to: "/profile",
			search: (old) => ({ ...old, tab: tab }),
			replace: true,
		});
	};

	return (
		<Content pageName="Profile">
			{/*<Header title="Profile" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="relative flex flex-row items-center w-full p-8 rounded-md border border-border/50">
					<div className="flex flex-row items-center w-full gap-4">
						<div className="inline-flex justify-center items-center w-[80px] h-[80px] rounded-full bg-primary-600 text-primary-foreground">
							<IconScale size={32} stroke={2} />
						</div>
						<div className="inline-flex flex-col w-auto">
							<div className="inline-flex flex-row items-center w-auto gap-1">
								<span className="font-bold text-xl text-foreground-dark">{user?.full_name}</span>
								<Tooltip content="Bar Top-notcher 1950">
									<div className="inline-flex text-yellow-600">
										<IconAwardFilled size={20} stroke={2} />
									</div>
								</Tooltip>
							</div>
							<span className="text-base text-foreground/70">{user?.email}</span>
							<span className="text-base text-foreground/70">
								{data.roll_number && <span className="text-base text-foreground/70">Role No. {data.roll_number}</span>}
							</span>
						</div>
					</div>
					<div className="absolute top-4 right-4 inline-flex self-start shrink-0 flex-row w-auto ml-auto gap-2">
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<Button className="!rounded-full" type="button" size="icon" variant="ghost">
									<IconDots size={14} stroke={2} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuGroup>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Some Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Another Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">More Actions</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => alert(1)} dangerZone>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Danger Zone</span>
										</div>
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<Tabs value={currentTab} onValueChange={(value) => setTab(value)}>
						<TabsList>
							<TabsTrigger value="cases">Cases</TabsTrigger>
							<TabsTrigger value="sessions">Active Sessions</TabsTrigger>
						</TabsList>

						<TabsContent value="cases">
							<Tabs defaultValue="all" onValueChange={(value) => setUrl(value)}>
								<TabsList>
									<TabsTrigger value="all">All Cases Involved</TabsTrigger>
									<TabsTrigger value="active">Active Cases Involved</TabsTrigger>
									<TabsTrigger value="closed">Closed Cases Involved</TabsTrigger>
								</TabsList>
								<TabsContent value={activeTab}>
									<ProfileCaseList cases={data.case_records || []} />
								</TabsContent>
							</Tabs>
						</TabsContent>

						<TabsContent value="sessions">
							<ProfileSessions />
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
